import { Fragment, useCallback, useState } from 'react';
import clsx from 'clsx';
import { Info } from '@phosphor-icons/react';
import type { IOption } from '@/types/features/questionnaire/question';
import HelperDialog from '@/features/funnel/questionnaires/components/HelperDialog';
import ExpandableTextarea from '@/features/funnel/questionnaires/components/ExpandableTextarea';

interface MultipleChoiceOptionsProps {
  options: IOption[];
  selectedOptions: string[];
  onChange: (selected: string[]) => void;
  setHasSafetyConcern?: (hasSafetyConcern: boolean) => void;
  expandableTextareaContent?: Record<string, string>;
  setExpandableTextareaContent?: (textareaContent: string, optionId: string) => void;
  expandableTextareaErrorMessage?: Record<string, string>;
  answerDisplayType?: number | string;
}

const MultipleChoiceOptions = ({
  options,
  selectedOptions,
  onChange,
  setHasSafetyConcern,
  expandableTextareaContent,
  setExpandableTextareaContent,
  expandableTextareaErrorMessage,
  answerDisplayType,
}: MultipleChoiceOptionsProps) => {
  const [modalState, setModalState] = useState<{
    option: IOption | null;
    type: 'moreInfo' | 'safetyConcern' | null;
  }>({ option: null, type: null });

  const openModal = useCallback((option: IOption, type: 'moreInfo' | 'safetyConcern') => {
    setModalState({ option, type });
  }, []);

  const closeModal = useCallback(() => {
    setModalState({ option: null, type: null });
  }, []);

  const handleSelect = useCallback(
    (optionId: string) => {
      const isSelected = selectedOptions.includes(optionId);
      const selectedOption = options.find((opt) => opt.id.toString() === optionId);

      let newSelection;
      if (selectedOption?.none_answer) {
        newSelection = isSelected ? [] : [optionId];
        setHasSafetyConcern?.(false);
      } else {
        newSelection = selectedOptions.some(
          (id) => options.find((opt) => opt.id.toString() === id)?.none_answer,
        )
          ? [optionId]
          : isSelected
            ? selectedOptions.filter((id) => id !== optionId)
            : [...selectedOptions, optionId];
      }

      onChange(newSelection);

      if (!isSelected) {
        if (selectedOption?.end_progress) {
          setHasSafetyConcern?.(true);
          openModal(selectedOption, 'safetyConcern');
        }
      } else {
        const hasConcern = options.some(
          (opt) => newSelection.includes(opt.id.toString()) && opt.end_progress,
        );
        setHasSafetyConcern?.(hasConcern);
      }
    },
    [onChange, options, selectedOptions, setHasSafetyConcern, openModal],
  );

  const handleTextareaContentChange = (content: string, optionId: string) => {
    setExpandableTextareaContent?.(content, optionId);
  };

  const { option: selectedOption, type: modalType } = modalState;
  const isSafetyConcern = modalType === 'safetyConcern';

  return (
    <div className="flex flex-col w-full space-y-8 tablet:space-y-12">
      <h3 className="text-body text-silver -mt-24 mb-32 tablet:mb-[40px]">
        Select all that apply.
      </h3>

      {options.map((option) => {
        const optionId = option.id.toString();
        const isSelected = selectedOptions.includes(optionId);
        const isImageType = [1, 'image'].includes(answerDisplayType || '');

        return (
          <Fragment key={optionId}>
            <label
              className={clsx(
                'flex flex-col w-full relative bg-paper text-ashgray text-body min-h-[54px] tablet:min-h-[74px] rounded-[8px] tablet:rounded-[12px] border transition-all',
                isSelected
                  ? `font-bold border-amberblack ${option.need_more_info ? 'mb-[6px]' : ''}`
                  : 'border-off-white',
              )}
            >
              <div
                className={`flex items-center justify-between ${isImageType ? 'gap-[10px]' : 'gap-16 tablet:gap-24'} p-16 tablet:p-[24px] ${option.helper_text ? 'pb-12 tablet:pb-20' : ''}`}
              >
                <div className="flex items-center">
                  {isImageType && (
                    <div
                      className={`w-[106px] tablet:w-[153px] h-[113px] tablet:h-[143px] flex-shrink-0 bg-off-gray-soft rounded-tl-[8px] tablet:rounded-tl-[12px] ${option.helper_text ? '-ml-16 tablet:-ml-24 -mt-16 tablet:-mt-24 -mb-12 tablet:-mb-20' : '-m-16 tablet:-m-24 rounded-bl-[8px] tablet:rounded-bl-[12px]'} mr-16 tablet:mr-24`}
                    >
                      <img src={option.attachment} alt="Head image" className="w-full h-full" />
                    </div>
                  )}
                  <span>{option.answer_text}</span>
                </div>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleSelect(optionId)}
                  className={`
                    flex-none !w-[14px] !h-[14px] tablet:!w-[18px] tablet:!h-[18px]
                    !rounded-full border-none after:!bg-none
                    ${isSelected ? '!bg-amberblack' : '!bg-off-white'}
                  `}
                />
              </div>

              {option.helper_text && (
                <div
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openModal(option, 'moreInfo');
                  }}
                  className="text-body-sm tablet:text-body-xs font-normal bg-off-gray px-16 tablet:px-24 pt-[10px] tablet:pt-12 pb-12 tablet:pb-[14px] cursor-pointer rounded-b-[8px] tablet:rounded-b-[12px]"
                >
                  <span className="text-ashgray flex items-center -ml-[3.23px] gap-[4.77px] tablet:gap-[6.385px]">
                    <Info size={17.24} /> See more info
                  </span>
                </div>
              )}
            </label>
            <ExpandableTextarea
              textareaContent={expandableTextareaContent?.[optionId] || ''}
              setTextareaContent={(content) => handleTextareaContentChange(content, optionId)}
              isVisible={isSelected && option.need_more_info}
              questionTitle={option.user_text_title || ''}
              textAreaPlaceHolder={option.user_text_placeholder}
            />
            {expandableTextareaErrorMessage &&
              expandableTextareaErrorMessage[option.id] &&
              option.need_more_info &&
              isSelected &&
              !expandableTextareaContent?.[optionId] && (
                <p role="alert" className="text-xs text-[var(--color-error)] ml-[4px]">
                  {expandableTextareaErrorMessage[option.id]}
                </p>
              )}
          </Fragment>
        );
      })}

      {selectedOption && modalType && (
        <HelperDialog
          isOpen
          isSafetyConcern={isSafetyConcern}
          title={selectedOption.answer_text}
          description={selectedOption.helper_text}
          buttonText="Return to Question"
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default MultipleChoiceOptions;
